/**
 * WorkspaceContext — 1:1 von InvoiceAI uebernommen
 * (../../invoiceai/frontend/src/contexts/WorkspaceContext.tsx).
 *
 * - Liest `?workspace_id=...` aus der URL beim ersten Mount UND entfernt
 *   den Param sofort via history.replaceState. Dadurch kann der Param
 *   nicht in späteren return_url-Captures wieder anhaengen → kein
 *   ?workspace_id=A&workspace_id=A&workspace_id=A-Loop.
 * - Persistiert activeWorkspaceId in localStorage.
 * - Auto-select des ersten Workspaces, wenn keiner gesetzt oder der
 *   gespeicherte nicht mehr in den Memberships ist.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { redirectToHub } from "@/lib/auth";

const STORAGE_KEY = "paypilot_active_workspace_id";
const LEGACY_CLIENT_KEY = "paypilot_active_client_id";
const URL_PARAM = "workspace_id";

// Soft-Migration: alten client_id-Key beim ersten Modul-Load entfernen.
// Workspace = Mandant nach Refactor — kein clientId-Layer mehr.
try {
  if (typeof window !== "undefined" && window.localStorage.getItem(LEGACY_CLIENT_KEY) !== null) {
    window.localStorage.removeItem(LEGACY_CLIENT_KEY);
  }
} catch {
  /* ignore */
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  handle: string;
  role: string;
  isAdmin: boolean;
}

interface WorkspaceContextValue {
  activeWorkspace: WorkspaceSummary | null;
  activeWorkspaceId: string | null;
  setActiveWorkspace: (id: string) => void;
  workspaces: WorkspaceSummary[];
  activeRole: string | null;
  isLoading: boolean;
  returnToHub: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/**
 * Liest `?workspace_id=...` aus der URL und raeumt den Parameter weg
 * (ohne Full-Reload, damit TanStack-Query-Cache erhalten bleibt).
 */
function consumeWorkspaceIdFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const wsId = url.searchParams.get(URL_PARAM);
    if (!wsId) return null;
    // Alle Vorkommen entfernen (defensive: bei einem fehlerhaften Loop
    // koennten mehrere drinstehen).
    while (url.searchParams.has(URL_PARAM)) {
      url.searchParams.delete(URL_PARAM);
    }
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + url.search + url.hash,
    );
    return wsId;
  } catch {
    return null;
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { memberships, isLoading: authLoading } = useAuthContext();

  const workspaces = useMemo<WorkspaceSummary[]>(
    () =>
      memberships.map((m) => ({
        id: m.workspace_id,
        name: m.workspace_display_name || m.workspace_handle || "Workspace",
        handle: m.workspace_handle,
        role: m.role,
        isAdmin: m.role === "OWNER" || m.role === "ADMIN",
      })),
    [memberships],
  );

  // Initial: URL-Param hat Vorrang vor localStorage.
  const [activeId, setActiveId] = useState<string | null>(() => {
    const fromUrl = consumeWorkspaceIdFromUrl();
    if (fromUrl) {
      try {
        localStorage.setItem(STORAGE_KEY, fromUrl);
      } catch {
        /* ignore */
      }
      return fromUrl;
    }
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Auto-select first workspace when none set or stored one is no longer accessible.
  useEffect(() => {
    if (workspaces.length === 0) return;
    const exists = activeId && workspaces.some((w) => w.id === activeId);
    if (!exists) {
      const next = workspaces[0].id;
      setActiveId(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
    }
  }, [workspaces, activeId]);

  const setActiveWorkspace = useCallback((id: string) => {
    setActiveId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const returnToHub = useCallback(() => {
    redirectToHub();
  }, []);

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeId) ?? null,
    [workspaces, activeId],
  );

  const activeRole = useMemo(() => {
    if (!activeId) return null;
    const m = memberships.find((m) => m.workspace_id === activeId);
    return m?.role ?? null;
  }, [activeId, memberships]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      activeWorkspace,
      activeWorkspaceId: activeWorkspace ? activeWorkspace.id : null,
      setActiveWorkspace,
      workspaces,
      activeRole,
      isLoading: authLoading,
      returnToHub,
    }),
    [
      activeWorkspace,
      setActiveWorkspace,
      workspaces,
      activeRole,
      authLoading,
      returnToHub,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useActiveWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useActiveWorkspace must be used inside WorkspaceProvider");
  }
  return ctx;
}

/** Backward-compat alias for existing code that uses `useWorkspace()`. */
export function useWorkspace(): WorkspaceContextValue {
  return useActiveWorkspace();
}
